import { BlockedPhoneNumber } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { DELETE_BLOCKED_PHONE_NUMBER_QUERY } from 'src/graphql/entities/blocked-phone-number';
import { ConditionInput } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

export interface DeleteBlockedPhoneNumberMutation {
  deleteBlockedPhoneNumber: Pick<BlockedPhoneNumber, 'id'>[];
}

export type DeleteBlockedPhoneNumberMutationParams = {
  conditions: ConditionInput[];
};

export const useDeleteBlockedPhoneNumber = () => {
  const { showErrorAlert } = useNotifications();
  const [deleteBlockedPhoneNumberMutation, { loading: isDeleting }] = useMutation<
    DeleteBlockedPhoneNumberMutation,
    DeleteBlockedPhoneNumberMutationParams
  >(DELETE_BLOCKED_PHONE_NUMBER_QUERY, {
    update(cache, { data }) {
      if (!data?.deleteBlockedPhoneNumber?.length) {
        return;
      }
      data.deleteBlockedPhoneNumber.forEach((phoneNumber) => {
        const id = cache.identify({
          __typename: 'BlockedPhoneNumber',
          id: phoneNumber.id,
        });
        cache.evict({ id });
      });
      cache.gc();
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to deleting blocked phone number, please try again');
    },
  });

  const deleteBlockedPhoneNumbersById = useCallback(
    async (blockedPhoneNumberIds: string[]) => {
      await deleteBlockedPhoneNumberMutation({
        variables: {
          conditions: [
            {
              attribute: 'blockedPhoneNumberId',
              operator: 'in',
              value: blockedPhoneNumberIds.join(','),
            },
          ],
        },
        optimisticResponse: {
          deleteBlockedPhoneNumber: blockedPhoneNumberIds.map((id) => ({
            __typename: 'BlockedPhoneNumber',
            id,
          })),
        },
      });
    },
    [deleteBlockedPhoneNumberMutation],
  );

  return {
    deleteBlockedPhoneNumbersById,
    isDeleting,
  };
};
